import { classifyIp, type IpKind } from "./ipClassification";
import {
  EMPTY_IP_FLOW_MATRIX,
  parseFlowTimestamp,
  type IpFlowCenter,
  type IpFlowEvent,
  type IpFlowLink,
  type IpFlowMatrix,
  type IpFlowNode,
  type IpFlowStats,
} from "./ipFlowMatrix";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function asOptionalNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const parsed = asNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseIpKind(value: unknown, ip = ""): IpKind {
  const kind = asString(value).toLowerCase();
  if (kind === "internal" || kind === "external") return kind;
  return ip ? classifyIp(ip) : "external";
}

function parseCenter(value: unknown): IpFlowCenter | null {
  const raw = asRecord(value);
  if (!raw) return null;
  return {
    timeZone: asString(raw.timeZone),
    latitude: asOptionalNumber(raw.latitude),
    longitude: asOptionalNumber(raw.longitude),
    label: asString(raw.label),
  };
}

function parseNode(value: unknown): IpFlowNode | null {
  const raw = asRecord(value);
  const ip = asString(raw?.ip);
  if (!raw || !ip) return null;
  return {
    ip,
    kind: parseIpKind(raw.kind, ip),
    latitude: asOptionalNumber(raw.latitude),
    longitude: asOptionalNumber(raw.longitude),
    city: asString(raw.city),
    countryCode: asString(raw.countryCode),
    asSource: asNumber(raw.asSource),
    asDestination: asNumber(raw.asDestination),
    firstSeen: parseFlowTimestamp(raw.firstSeen),
  };
}

function parseEvent(value: unknown): IpFlowEvent | null {
  const raw = asRecord(value);
  const source = asString(raw?.source);
  const destination = asString(raw?.destination);
  if (!raw || !source || !destination) return null;
  return {
    at: parseFlowTimestamp(raw.at),
    source,
    destination,
    severity: asString(raw.severity),
    sourceKind: parseIpKind(raw.sourceKind, source),
    destinationKind: parseIpKind(raw.destinationKind, destination),
  };
}

function linksFromEvents(events: IpFlowEvent[]): IpFlowLink[] {
  const pairCounts = new Map<string, IpFlowLink>();
  for (const event of events) {
    const key = `${event.source}\u0000${event.destination}`;
    const existing = pairCounts.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    pairCounts.set(key, {
      source: event.source,
      destination: event.destination,
      count: 1,
      sourceKind: event.sourceKind,
      destinationKind: event.destinationKind,
    });
  }
  return [...pairCounts.values()].sort((left, right) => right.count - left.count);
}

function parseStats(
  value: unknown,
  nodes: IpFlowNode[],
  events: IpFlowEvent[],
  recordCount: number,
): IpFlowStats {
  const raw = asRecord(value);
  const internalCount = nodes.filter((node) => node.kind === "internal").length;
  const externalCount = nodes.filter((node) => node.kind === "external").length;
  return {
    recordCount: asNumber(raw?.recordCount, recordCount),
    eventCount: asNumber(raw?.eventCount, events.length),
    internalCount: asNumber(raw?.internalCount, internalCount),
    externalCount: asNumber(raw?.externalCount, externalCount),
  };
}

export function nodeHasCoordinates(node: Pick<IpFlowNode, "latitude" | "longitude">): boolean {
  return node.latitude != null && node.longitude != null;
}

export function ipFlowHasCoordinates(matrix?: IpFlowMatrix | null): boolean {
  if (!matrix) return false;
  if (matrix.center && nodeHasCoordinates(matrix.center)) return true;
  return matrix.nodes.some(nodeHasCoordinates);
}

export function nodePlaceLabel(
  node?: Pick<IpFlowNode, "city" | "countryCode" | "kind">,
  center?: IpFlowCenter | null,
): string {
  if (!node) return "";
  const city = node.city?.trim() ?? "";
  const country = node.countryCode?.trim() ?? "";
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  if (node.kind === "internal") return center?.label?.trim() ?? "";
  return "";
}

export type IpFlowGeoPoint = {
  longitude: number;
  latitude: number;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function resolveIpFlowCoordinates(
  nodes: IpFlowNode[],
  center?: IpFlowCenter | null,
): Map<string, IpFlowGeoPoint> {
  const raw: Array<{ ip: string; latitude: number; longitude: number }> = [];
  for (const node of nodes) {
    const latitude = node.latitude ?? center?.latitude ?? null;
    const longitude = node.longitude ?? center?.longitude ?? null;
    if (latitude == null || longitude == null) continue;
    raw.push({ ip: node.ip, latitude, longitude });
  }

  const positions = new Map<string, IpFlowGeoPoint>();
  for (const point of raw) {
    positions.set(point.ip, {
      latitude: point.latitude,
      longitude: point.longitude,
    });
  }

  if (center?.latitude != null && center.longitude != null) {
    positions.set("__center__", {
      latitude: center.latitude,
      longitude: center.longitude,
    });
  }

  return positions;
}

const MIN_LAT_SPAN = 4;
const MIN_LNG_SPAN = 6;
const VIEW_PAD = 0.32;

export type IpFlowMapBounds = [[number, number], [number, number]];

export function fitIpFlowBounds(
  points: Iterable<IpFlowGeoPoint>,
): IpFlowMapBounds {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  let count = 0;
  for (const point of points) {
    count += 1;
    minLat = Math.min(minLat, point.latitude);
    maxLat = Math.max(maxLat, point.latitude);
    minLng = Math.min(minLng, point.longitude);
    maxLng = Math.max(maxLng, point.longitude);
  }
  if (count === 0) {
    return [
      [-168, 78],
      [188, -56],
    ];
  }

  let latSpan = maxLat - minLat;
  let lngSpan = maxLng - minLng;
  if (latSpan < MIN_LAT_SPAN) {
    const mid = (minLat + maxLat) / 2;
    minLat = mid - MIN_LAT_SPAN / 2;
    maxLat = mid + MIN_LAT_SPAN / 2;
    latSpan = MIN_LAT_SPAN;
  }
  if (lngSpan < MIN_LNG_SPAN) {
    const mid = (minLng + maxLng) / 2;
    minLng = mid - MIN_LNG_SPAN / 2;
    maxLng = mid + MIN_LNG_SPAN / 2;
    lngSpan = MIN_LNG_SPAN;
  }

  minLat = clamp(minLat - latSpan * VIEW_PAD, -85, 85);
  maxLat = clamp(maxLat + latSpan * VIEW_PAD, -85, 85);
  minLng = clamp(minLng - lngSpan * VIEW_PAD, -180, 180);
  maxLng = clamp(maxLng + lngSpan * VIEW_PAD, -180, 180);

  return [
    [minLng, maxLat],
    [maxLng, minLat],
  ];
}

export type IpFlowMapView = {
  center: [number, number];
  zoom: number;
};

export function fitIpFlowView(points: Iterable<IpFlowGeoPoint>): IpFlowMapView {
  const [[minLng, maxLat], [maxLng, minLat]] = fitIpFlowBounds(points);
  const lngSpan = Math.max(maxLng - minLng, 0.01);
  const latSpan = Math.max(maxLat - minLat, 0.01);
  return {
    center: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
    zoom: clamp(Math.min(360 / lngSpan, 170 / latSpan), 1.15, 24),
  };
}

const SAME_LOCATION_DEG = 0.05;

export function sameIpFlowLocation(
  left?: IpFlowGeoPoint | null,
  right?: IpFlowGeoPoint | null,
): boolean {
  if (!left || !right) return false;
  return (
    Math.abs(left.latitude - right.latitude) < SAME_LOCATION_DEG &&
    Math.abs(left.longitude - right.longitude) < SAME_LOCATION_DEG
  );
}

export function loopIpFlowCoords(
  point: IpFlowGeoPoint,
  radius = 0.28,
): [number, number][] {
  const latRad = (point.latitude * Math.PI) / 180;
  const lngScale = Math.max(Math.cos(latRad), 0.2);
  const steps = 12;
  const coords: [number, number][] = [];
  for (let index = 0; index <= steps; index += 1) {
    const t = index / steps;
    const angle = Math.PI * 0.35 + t * Math.PI * 0.9;
    const reach = Math.sin(t * Math.PI) * radius;
    coords.push([
      clamp(point.longitude + (reach * Math.cos(angle)) / lngScale, -180, 180),
      clamp(point.latitude + reach * Math.sin(angle), -85, 85),
    ]);
  }
  return coords;
}

export function parseDashboardIpFlowResponse(response: unknown): IpFlowMatrix {
  const raw = asRecord(response);
  if (!raw) return { ...EMPTY_IP_FLOW_MATRIX, stats: { ...EMPTY_IP_FLOW_MATRIX.stats! } };

  const nodes = Array.isArray(raw.nodes)
    ? raw.nodes
        .map(parseNode)
        .filter((node): node is IpFlowNode => Boolean(node))
        .sort(
          (left, right) =>
            left.firstSeen - right.firstSeen || left.ip.localeCompare(right.ip),
        )
    : [];
  const events = Array.isArray(raw.events)
    ? raw.events
        .map(parseEvent)
        .filter((event): event is IpFlowEvent => Boolean(event))
        .sort(
          (left, right) =>
            left.at - right.at ||
            left.source.localeCompare(right.source) ||
            left.destination.localeCompare(right.destination),
        )
    : [];
  const links = linksFromEvents(events);
  const recordCount = asNumber(asRecord(raw.stats)?.recordCount, events.length);

  return {
    window: asString(raw.window) || "24h",
    generatedAt: asString(raw.generatedAt),
    center: parseCenter(raw.center),
    nodes,
    links,
    events,
    recordCount,
    pairCount: links.reduce((sum, link) => sum + link.count, 0),
    stats: parseStats(raw.stats, nodes, events, recordCount),
  };
}
